import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { faEdit, faTrash, faSearch, faTrashAlt, faPaperPlane, faChartBar } from '@fortawesome/free-solid-svg-icons';
import Pagination from '../../components/Pagination';
import ConfirmationModal from '../../components/ConfirmationModal';
import TextInput from '../../components/TextInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import blogApi from '../../api/blogApi';
import { ROUTES, API_ENDPOINTS } from '../../constants';
import apiClient from '../../api/axiosConfig';
import { useToast } from '../../contexts/ToastContext';

const Blogs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToast();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [itemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [blogToDeleteId, setBlogToDeleteId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [error, setError] = useState(null);

  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [blogToNotify, setBlogToNotify] = useState(null);
  const [sendingNotificationForBlogId, setSendingNotificationForBlogId] = useState(null);

  const [lastPage, setLastPage] = useState(1);

  const queryParams = new URLSearchParams(location.search);
  const currentPage = parseInt(queryParams.get('page')) || 1;

  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  const fetchBlogs = useCallback(async (page, perPage, search) => {
    setLoading(true);
    setError(null);
    try {
      const response = await blogApi.getBlogs({ page, perPage, search, includeTrashed: false });
      setBlogs(response.data);
      setLastPage(response.pagination.last_page);
    } catch (err) {
      console.error('Failed to fetch blogs:', err);
      setError('Failed to load blogs. Please try again later.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBlogs(currentPage, itemsPerPage, debouncedSearchTerm);
  }, [fetchBlogs, currentPage, itemsPerPage, debouncedSearchTerm]);

  const handleAddBlog = () => {
    navigate(ROUTES.BLOGS_CREATE);
  };

  const handleGoToTrash = () => {
    navigate(ROUTES.BLOGS_TRASH);
  };

  const handleEditBlog = (id) => {
    navigate(ROUTES.BLOGS_EDIT.replace(':id', id));
  };

  const handleViewAnalyticsClick = (id) => {
    navigate(`/blogs/${id}/analytics`);
  };

  const handleDeleteClick = (id) => {
    setBlogToDeleteId(id);
    setIsModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    setIsModalOpen(false);
    if (blogToDeleteId) {
      setLoading(true);
      setError(null);
      try {
        await blogApi.trashBlog(blogToDeleteId);
        addToast('Blog moved to trash successfully!', 'success');
        await fetchBlogs(currentPage, itemsPerPage, debouncedSearchTerm);
      } catch (err) {
        console.error('Failed to trash blog:', err);
        addToast(err.response?.data?.message || 'Failed to move blog to trash. Please try again.', 'error');
        setError('Failed to move blog to trash. Please try again.');
      } finally {
        setLoading(false);
        setBlogToDeleteId(null);
      }
    }
  };

  const handleCancelDelete = () => {
    setIsModalOpen(false);
    setBlogToDeleteId(null);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('page', 1);
    navigate(`${location.pathname}?${searchParams.toString()}`);
  };

  const handleSendNotificationClick = (blog) => {
    setBlogToNotify(blog);
    setIsNotificationModalOpen(true);
  };

  const handleConfirmSendNotification = async () => {
    setIsNotificationModalOpen(false);
    if (!blogToNotify) return;

    setSendingNotificationForBlogId(blogToNotify.id); // Set spinner for this blog
    try {
      // First API call: Queue the notification
      const queueResponse = await apiClient.post(API_ENDPOINTS.BLOG_SEND_NOTIFICATION(blogToNotify.id));

      if (queueResponse.data.success) {
        addToast(queueResponse.data.message || 'Notification queued successfully.', 'success');
      } else {
        addToast(queueResponse.data.message || 'Failed to queue notification.', 'error');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Error sending notification.';
      addToast(errorMessage, 'error');
    } finally {
      setSendingNotificationForBlogId(null); // Clear spinner
      setBlogToNotify(null);
    }
  };

  const handleCancelSendNotification = () => {
    setIsNotificationModalOpen(false);
    setBlogToNotify(null);
  };

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white flex flex-col min-h-[calc(100vh-100px)]">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6">
        <h1 className="text-3xl font-bold text-text-white mb-4 sm:mb-0">Blog Management</h1>
        <div className="flex space-x-4">
          <Button
            onClick={handleAddBlog}
            className="bg-accent-red hover:bg-red-700 text-white"
          >
            Add New Blog
          </Button>
          <Button
            onClick={handleGoToTrash}
            className="bg-gray-600 hover:bg-gray-700 text-white"
            icon={faTrashAlt}
            tooltipId="trash-blogs-tooltip"
            tooltipContent="View Trashed Blogs"
          >
            Trash
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <TextInput
          label="Search Blogs"
          id="search-blogs"
          name="search-blogs"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by title..."
          icon={faSearch}
        />
      </div>

      {loading ? (
        <SkeletonLoader type="table" count={itemsPerPage} />
      ) : error ? (
        <div className="text-red-500 text-center py-4">{error}</div>
      ) : (
        <>
          <div className="overflow-x-auto flex-grow">
            <table className="min-w-full bg-gray-800 border border-gray-700">
              <thead>
                <tr>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">ID</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Image</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Title</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Active</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Updated At</th>
                  <th className="py-3 px-4 border-b border-gray-700 text-left text-sm font-medium text-text-gray">Actions</th>
                </tr>
              </thead>
              <tbody>
                {blogs.length > 0 ? (
                  blogs.map((blog) => (
                    <tr key={blog.id} className="hover:bg-gray-700">
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{blog.id}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {blog.image && (
                          <img src={blog.image} alt={blog.title} className="h-10 w-10 object-cover rounded-full" />
                        )}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">{blog.title}</td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {blog.is_active ? 'Yes' : 'No'}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm text-text-white">
                        {new Date(blog.updated_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </td>
                      <td className="py-3 px-4 border-b border-gray-700 text-sm flex items-center flex-nowrap">
                        <Button
                          onClick={() => handleEditBlog(blog.id)}
                          className="text-yellow-500 hover:text-yellow-600 mr-3"
                          icon={faEdit}
                          tooltipId={`edit-blog-tooltip-${blog.id}`}
                          tooltipContent="Edit"
                        />
                        <Button
                          onClick={() => handleDeleteClick(blog.id)}
                          className="text-red-600 hover:text-red-700 mr-3"
                          icon={faTrash}
                          tooltipId={`delete-blog-tooltip-${blog.id}`}
                          tooltipContent="Delete"
                        />
                        {blog.is_active && (
                          <>
                            <Button
                              onClick={() => handleSendNotificationClick(blog)}
                              className="text-blue-500 hover:text-blue-600 mr-3"
                              icon={faPaperPlane}
                              spin={sendingNotificationForBlogId === blog.id}
                              tooltipId={`send-notification-tooltip-${blog.id}`}
                              tooltipContent={sendingNotificationForBlogId === blog.id ? 'Sending...' : 'Send Notification'}
                              disabled={sendingNotificationForBlogId === blog.id}
                            />
                            <Button
                              onClick={() => handleViewAnalyticsClick(blog.id)}
                              className="text-purple-500 hover:text-purple-600"
                              icon={faChartBar}
                              tooltipId={`view-analytics-tooltip-${blog.id}`}
                              tooltipContent="View Analytics"
                            />
                          </>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="py-3 px-4 text-center text-sm text-text-gray">No blogs found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={currentPage}
            totalPages={lastPage}
          />
        </>
      )}

      <ConfirmationModal
        isOpen={isModalOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to move this blog post to trash?"
      />

      <ConfirmationModal
        isOpen={isNotificationModalOpen}
        onClose={handleCancelSendNotification}
        onConfirm={handleConfirmSendNotification}
        title="Confirm Send Notification"
        message={`Are you sure you want to send a newsletter notification for "${blogToNotify?.title}" to all subscribers?`}
      />
    </div>
  );
};

export default Blogs;